// Configure Foreign CF DNS - the wizard shell.
//
// This component owns ALL server state for one run (run + domains + stages) and
// hands it down to the step components. The steps never fetch; they call the
// callbacks in FdStepProps and let the shell re-read. One reader means one
// truth: after any write, what the operator sees is what the server holds.
//
// SECURITY: the customer's Cloudflare token is write-only from the browser's
// point of view. It goes up through fdApi.setKey and never comes back in any
// response, so nothing in this tree can render it. StepKey clears its own input
// on success and holds no copy.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './ForeignDns.css';
import { StepChoices } from './foreign-dns/StepChoices';
import { StepCodeGrid } from './foreign-dns/StepCodeGrid';
import { StepDomains } from './foreign-dns/StepDomains';
import { StepKey } from './foreign-dns/StepKey';
import { StepOverview } from './foreign-dns/StepOverview';
import {
  findStage,
  isZoneNotFound,
  useEscapeToClose,
  type FdStepProps,
  type FdToastKind,
} from './foreign-dns/shared';
import {
  fdApi,
  fdErrorMessage,
  type FdDomain,
  type FdRunDetail,
  type FdRunStatus,
  type FdStage,
} from './foreignDnsApi';

const TOTAL_STEPS = 8;

const POLL_MS = 3000;

const TOAST_MS = 8000;

/**
 * How long to keep polling after a stage was enqueued but before any worker has
 * flipped its row to `running`.
 *
 * The steady-state rule is "poll only while a stage row is running, and stop as
 * soon as none are". Between the enqueue and the worker's startStage there is a
 * gap where nothing is running yet, so that rule alone would leave the wizard
 * frozen right after the operator clicked Apply. This window covers exactly that
 * gap: it is armed only by an enqueue, it is torn down the instant a row goes
 * `running`, and it expires on its own so a job that never gets picked up (queue
 * paused, no worker with WORKER_ENABLE_DOMAIN) cannot leave a poll running for
 * the rest of the session.
 */
const PICKUP_WINDOW_MS = 60_000;

const STEPS: { n: number; label: string }[] = [
  { n: 1, label: 'Cloudflare key' },
  { n: 2, label: 'Domains' },
  { n: 3, label: 'Purge' },
  { n: 4, label: 'Forwarding' },
  { n: 5, label: 'Basic PI DNS' },
  { n: 6, label: 'Verification' },
  { n: 7, label: 'DKIM' },
  { n: 8, label: 'Overview' },
];

/**
 * Run status to badge variant. Same mapping as the run list (ForeignDnsRuns),
 * repeated here rather than exported from it because the list file is not in
 * this change set. Sage for finished, blue for in flight (the app-wide running
 * colour), muted for not-started and for a deliberately abandoned run.
 */
const STATUS_BADGE: Record<FdRunStatus, string> = {
  draft: 'muted',
  in_progress: 'info',
  completed: 'ok',
  cancelled: 'muted',
};

const STATUS_LABEL: Record<FdRunStatus, string> = {
  draft: 'draft',
  in_progress: 'in progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

interface Toast {
  type: FdToastKind;
  message: string;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }) : '-';
}

/** Every non-zone_not_found domain has settled the basic stage. This is the
 *  prerequisite for steps 6 and up. `skipped` counts: a domain the stage
 *  deliberately passed over is settled, not outstanding. */
function basicSettled(domains: FdDomain[]): boolean {
  const reachable = domains.filter((d) => !isZoneNotFound(d));
  return (
    reachable.length > 0 &&
    reachable.every((d) => d.basic_status === 'ok' || d.basic_status === 'skipped')
  );
}

export function ForeignDnsRunDetail({
  runId,
  onBack,
}: {
  runId: string;
  onBack: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<FdRunDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [pickupUntil, setPickupUntil] = useState<number | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [stepBusy, setStepBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);

  /**
   * One toast at a time, one timer at a time.
   *
   * The previous timer has to be cancelled, not just outlived: without this, a
   * toast raised near the end of an earlier toast's 8s window is wiped by that
   * earlier timer firing. The `alreadyRunning` warning exists ONLY as a toast,
   * so it is precisely the message a stray timer would truncate. Cleared on
   * unmount too, so the callback cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((type: FdToastKind, message: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  /**
   * Monotonic request id, so a slow getRun can never overwrite a newer one.
   *
   * Not merely cosmetic. Poll and action responses interleave: re-run a stage
   * and a stale `done` response landing after the fresh `running` one flips
   * anyRunning back to false, tears the poll down, and freezes the wizard on
   * stale content until the operator navigates away. Dropping every
   * non-latest response is what stops that.
   */
  const reloadSeq = useRef(0);
  const reload = useCallback(async (): Promise<void> => {
    const seq = (reloadSeq.current += 1);
    try {
      const next = await fdApi.getRun(runId);
      if (seq !== reloadSeq.current) return;
      setDetail(next);
      setLoadError(null);
    } catch (err) {
      if (seq !== reloadSeq.current) return;
      setLoadError(fdErrorMessage(err));
    }
  }, [runId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const anyRunning = useMemo(
    () => (detail?.stages ?? []).some((s) => s.status === 'running'),
    [detail],
  );

  // A row went `running`, so the pickup window has done its job and the normal
  // rule takes over from here.
  useEffect(() => {
    if (anyRunning && pickupUntil !== null) setPickupUntil(null);
  }, [anyRunning, pickupUntil]);

  useEffect(() => {
    if (pickupUntil === null) return undefined;
    const timer = window.setTimeout(
      () => setPickupUntil(null),
      Math.max(0, pickupUntil - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [pickupUntil]);

  // The only poll in this feature: on while a stage row is `running` (or while
  // we are waiting for a just-enqueued job to be picked up), off otherwise.
  const polling = anyRunning || pickupUntil !== null;
  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => {
      void reload();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, reload]);

  const notifyEnqueued = useCallback(() => {
    setPickupUntil(Date.now() + PICKUP_WINDOW_MS);
  }, []);

  const goToStep = useCallback(
    async (step: number): Promise<void> => {
      setStepBusy(true);
      try {
        await fdApi.setChoices(runId, { current_step: step });
        await reload();
      } catch (err) {
        showToast('error', fdErrorMessage(err));
      } finally {
        setStepBusy(false);
      }
    },
    [runId, reload, showToast],
  );

  useEscapeToClose(confirmCancel, cancelling, () => setConfirmCancel(false));

  async function cancelRun(): Promise<void> {
    setCancelling(true);
    try {
      await fdApi.cancelRun(runId);
      setConfirmCancel(false);
      await reload();
      showToast('success', 'Run cancelled. The Cloudflare key was destroyed.');
    } catch (err) {
      showToast('error', fdErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  }

  if (!detail) {
    return (
      <div className="fd">
        <div className="fd-scroll">
          <div className="fd-runheader">
            <button type="button" className="fd-btn fd-btn--ghost fd-btn--small" onClick={onBack}>
              Back
            </button>
            <h1 className="fd-title">Run</h1>
          </div>
          {loadError ? (
            <>
              <div className="fd-toast fd-toast--error">{loadError}</div>
              <div className="fd-card">
                <div className="fd-card-body">
                  <div className="fd-actions">
                    <button
                      type="button"
                      className="fd-btn fd-btn--primary"
                      onClick={() => {
                        setRetrying(true);
                        void reload().finally(() => setRetrying(false));
                      }}
                      disabled={retrying}
                    >
                      {retrying ? 'Retrying...' : 'Retry'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="fd-card">
              <div className="fd-empty">Loading run...</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { run, domains, stages } = detail;
  const closed = run.status === 'completed' || run.status === 'cancelled';
  const hasZone = domains.some((d) => !isZoneNotFound(d));
  const settled = basicSettled(domains);

  /**
   * Why a step cannot be opened yet, or null when it can.
   *
   * Steps 1 and 2 are always reachable - re-entering the key and re-checking the
   * domain list are the two recoveries for everything else. From step 3 the run
   * needs at least one resolved zone, and from step 6 every reachable domain has
   * to have settled the basic stage.
   */
  function lockReason(step: number): string | null {
    // Going backwards is always allowed: a step already visited stays reachable
    // even if the data behind it has since changed.
    if (step <= run.current_step) return null;
    if (step <= 2) return null;
    if (!hasZone) {
      return 'Add the domain list first - no domain in this run has a Cloudflare zone yet.';
    }
    if (step >= 6 && !settled) {
      return 'Run the Basic PI DNS step first - some domains have not finished it.';
    }
    return null;
  }

  /**
   * The stage that backs a step, for the rail's error state. Steps 1, 4 and 8
   * have no stage of their own - they are a key, an answer and a summary.
   */
  function stageForStep(step: number): FdStage | null {
    switch (step) {
      case 2:
        return 'probe';
      case 3:
        return 'purge';
      case 5:
        return 'basic';
      case 6:
        return 'verification';
      case 7:
        return 'dkim';
      default:
        return null;
    }
  }

  /** Only a whole-stage `failed` paints the rail red. A per-domain failure does
   *  not: a zone_not_found row is explicitly non-fatal and the run carries on. */
  function stepFailed(step: number): boolean {
    const stage = stageForStep(step);
    return stage !== null && findStage(stages, stage)?.status === 'failed';
  }

  function railState(step: number): 'active' | 'error' | 'done' | 'locked' | null {
    // Active wins over error, because the step you are standing on already shows
    // the failure in full in its own body; the rail's job there is "you are
    // here". Everywhere else a broken stage outranks everything.
    if (step === run.current_step) return 'active';
    if (stepFailed(step)) return 'error';
    if (stepDone(step)) return 'done';
    if (lockReason(step) !== null) return 'locked';
    return null;
  }

  /**
   * Is this step's OWN work finished?
   *
   * The brief phrased `--done` as "its prerequisite is satisfied", which would
   * paint step 8 sage the moment the basic stage went green - before anything on
   * step 8 had been touched. Sage means done in this design system, so that
   * would be a false claim on the one signal the operator scans first. Done here
   * therefore means the step's own work landed, and the prerequisite drives
   * `--locked` only. Flagged in the task report.
   */
  function stepDone(step: number): boolean {
    switch (step) {
      case 1:
        return run.has_token;
      case 2:
        return domains.length > 0 && findStage(stages, 'probe')?.status === 'done';
      case 3:
        return (
          run.purge_choice === 'no' ||
          (run.purge_choice === 'yes' && findStage(stages, 'purge')?.status === 'done')
        );
      case 4:
        return (
          run.forwarding_choice === 'no' ||
          (run.forwarding_choice === 'yes' &&
            domains.length > 0 &&
            domains.every((d) => d.forwarding_url !== null))
        );
      case 5:
        return findStage(stages, 'basic') !== null && settled;
      case 6:
        return stageSettled('verification');
      case 7:
        return stageSettled('dkim');
      case 8:
        // The overview's own work is finishing the run, which is the act that
        // destroys the borrowed key. Nothing short of that has landed.
        return run.status === 'completed';
      default:
        return false;
    }
  }

  /** The stage has run and every reachable domain has settled it. `skipped`
   *  counts - a domain left blank on purpose is settled, not outstanding - and
   *  so does a zone_not_found row, which is excluded from `reachable`. */
  function stageSettled(stage: 'verification' | 'dkim'): boolean {
    if (findStage(stages, stage) === null) return false;
    const reachable = domains.filter((d) => !isZoneNotFound(d));
    return (
      reachable.length > 0 &&
      reachable.every((d) => {
        const status = stage === 'verification' ? d.verification_status : d.dkim_status;
        return status === 'ok' || status === 'skipped';
      })
    );
  }

  const stepProps: FdStepProps = {
    runId,
    detail,
    reload,
    notifyEnqueued,
    goToStep,
    toast: showToast,
  };

  function renderStep(): JSX.Element {
    switch (run.current_step) {
      case 1:
        return <StepKey {...stepProps} />;
      case 2:
        return <StepDomains {...stepProps} />;
      case 3:
        return <StepChoices {...stepProps} which="purge" />;
      case 4:
        return <StepChoices {...stepProps} which="forwarding" />;
      case 5:
        return <StepChoices {...stepProps} which="basic" />;
      // The two code grids are ONE component used twice, so React would
      // otherwise keep the same instance across a 6 -> 7 move and carry the
      // verification step's typed values, keyed by domain id, into the DKIM
      // grid. The keys force a real remount, so the local state and the four
      // refs are rebuilt rather than carried across. (They are not what flushes
      // a pending save - the unmount effect has `field` in its deps and would
      // fire on a props-only change anyway.)
      case 6:
        return (
          <StepCodeGrid
            key="verification"
            {...stepProps}
            field="verification_code"
            stage="verification"
          />
        );
      case 7:
        return <StepCodeGrid key="dkim" {...stepProps} field="dkim_value" stage="dkim" />;
      case 8:
      default:
        // POST /:id/choices is the only writer of current_step and its zod
        // schema caps it at 1..8, so the default arm is unreachable through the
        // API. The COLUMN has no check constraint, though, so a hand-run UPDATE
        // could land here; the overview is the safe place for that to end up,
        // because it reads the run and writes nothing until it is asked to.
        return <StepOverview {...stepProps} />;
    }
  }

  // A key that was verified once and is not held now. NOT simply `!has_token`:
  // a fresh draft has never had a key, and telling the operator theirs "has been
  // cleared" would send them looking for something that never happened.
  const keyCleared = !closed && !run.has_token && run.cf_token_verified_at !== null;
  const resolvedZones = domains.filter((d) => !isZoneNotFound(d)).length;

  return (
    <div className="fd">
      <div className="fd-scroll">
        <div className="fd-runheader">
          <button type="button" className="fd-btn fd-btn--ghost fd-btn--small" onClick={onBack}>
            Back
          </button>
          <h1 className="fd-title">{run.name}</h1>
          <span className={`fd-badge fd-badge--${STATUS_BADGE[run.status]}`}>
            {STATUS_LABEL[run.status]}
          </span>
          <div className="fd-actions-spacer" />
          {!closed && (
            <button
              type="button"
              className="fd-btn fd-btn--danger fd-btn--small"
              onClick={() => setConfirmCancel(true)}
            >
              Cancel run
            </button>
          )}
        </div>

        <div className="fd-runmeta">
          <span>
            <strong>Step</strong>
            {run.current_step} of {TOTAL_STEPS}
          </span>
          <span>
            <strong>Domains</strong>
            {domains.length}
          </span>
          <span>
            <strong>Cloudflare key</strong>
            {run.has_token ? `held, verified ${fmtDate(run.cf_token_verified_at)}` : 'not held'}
          </span>
          <span>
            <strong>Created</strong>
            {fmtDate(run.created_at)} by {run.created_by}
          </span>
        </div>

        {loadError && <div className="fd-toast fd-toast--error">{loadError}</div>}
        {toast && <div className={`fd-toast fd-toast--${toast.type}`}>{toast.message}</div>}

        {keyCleared && (
          <div className="fd-toast fd-toast--warn">
            This run's Cloudflare key has been cleared. Re-enter it on step 1 to continue.
          </div>
        )}

        {closed && (
          <div className="fd-toast fd-toast--info">
            This run is {run.status} and its Cloudflare key was destroyed. Everything typed in is
            kept for the record, but no further Cloudflare work can be done on it. Start a new run
            to configure more domains.
          </div>
        )}

        <div className="fd-rail">
          {STEPS.map((s) => {
            const state = railState(s.n);
            const reason = lockReason(s.n);
            const failed = stepFailed(s.n);
            return (
              <button
                key={s.n}
                type="button"
                className={`fd-rail-step${state ? ` fd-rail-step--${state}` : ''}`}
                disabled={reason !== null || stepBusy}
                title={reason ?? (failed ? `${s.label} - this stage failed` : s.label)}
                onClick={() => {
                  if (s.n !== run.current_step) void goToStep(s.n);
                }}
              >
                <span className="fd-rail-num">{s.n}</span>
                <span className="fd-rail-label">{s.label}</span>
              </button>
            );
          })}
        </div>

        {renderStep()}
      </div>

      {confirmCancel && (
        <div className="fd-modal-overlay">
          <div className="fd-modal">
            <div className="fd-modal-header">
              <h3 className="fd-modal-title">Cancel this run?</h3>
            </div>
            <div className="fd-modal-body">
              <p>
                The customer's Cloudflare key is destroyed immediately and this run will not accept
                a new one. Nothing already written to their DNS is undone.
              </p>
              <p>
                Every domain, verification code and DKIM value stays on the run as a record of what
                was done. {resolvedZones} zone(s) are currently resolved.
              </p>
            </div>
            <div className="fd-modal-footer">
              <button
                type="button"
                className="fd-btn fd-btn--danger"
                onClick={() => void cancelRun()}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel run and destroy the key'}
              </button>
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={() => setConfirmCancel(false)}
                disabled={cancelling}
              >
                Keep the run open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
